/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/landing/components/LandingFooter.tsx
 * PURPOSE: Landing Footer - Links columns + Copyright + Language toggle
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * DATE: 2025-12-28
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System V2.0
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/LOGIN-LANDING-REDESIGN-DESIGN.md
 */

import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./LandingFooter.module.css";

interface FooterColumn {
  titleKey: string;
  links: Array<{
    labelKey: string;
    href: string;
  }>;
}

const columns: FooterColumn[] = [
  {
    titleKey: "landing.footer.product.title",
    links: [
      { labelKey: "landing.footer.product.dashboard", href: "/login" },
      { labelKey: "landing.footer.product.attendance", href: "/login" },
      { labelKey: "landing.footer.product.scheduling", href: "/login" },
      { labelKey: "landing.footer.product.bhxh", href: "/login" },
    ],
  },
  {
    titleKey: "landing.footer.company.title",
    links: [
      { labelKey: "landing.footer.company.about", href: "#" },
      { labelKey: "landing.footer.company.blog", href: "#" },
      { labelKey: "landing.footer.company.careers", href: "#" },
    ],
  },
  {
    titleKey: "landing.footer.support.title",
    links: [
      { labelKey: "landing.footer.support.contact", href: "#contact" },
      { labelKey: "landing.footer.support.faq", href: "#" },
      { labelKey: "landing.footer.support.docs", href: "#" },
    ],
  },
  {
    titleKey: "landing.footer.legal.title",
    links: [
      { labelKey: "landing.footer.legal.terms", href: "#" },
      { labelKey: "landing.footer.legal.privacy", href: "#" },
    ],
  },
];

export const LandingFooter: React.FC = () => {
  const { t, i18n } = useTranslation();

  const setLang = async (lng: "vi" | "en") => {
    await i18n.changeLanguage(lng);
    localStorage.setItem("nqh-language", lng);
    document.documentElement.lang = lng;
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* Brand Column */}
        <div className={styles.brandColumn}>
          <div className={styles.brand}>
            <span className={styles.logo}>☕</span>
            <span className={styles.brandText}>NQH-Bot</span>
          </div>
          <p className={styles.brandDesc}>
            {t("landing.footer.brandDesc")}
          </p>
        </div>

        {/* Link Columns */}
        {columns.map((column, index) => (
          <div key={index} className={styles.column}>
            <h4 className={styles.columnTitle}>{t(column.titleKey)}</h4>
            <ul className={styles.links}>
              {column.links.map((link, linkIndex) => (
                <li key={linkIndex}>
                  <a href={link.href} className={styles.link}>
                    {t(link.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom Bar */}
      <div className={styles.bottom}>
        <p className={styles.copyright}>
          {t("landing.footer.copyright")}
        </p>
        <div className={styles.langToggle}>
          <button
            type="button"
            className={`${styles.langButton} ${i18n.language.startsWith("vi") ? styles.langActive : ""}`}
            onClick={() => setLang("vi")}
          >
            VI
          </button>
          <button
            type="button"
            className={`${styles.langButton} ${i18n.language.startsWith("en") ? styles.langActive : ""}`}
            onClick={() => setLang("en")}
          >
            EN
          </button>
        </div>
      </div>
    </footer>
  );
};
